/**
 * Oversettelser: review the machine English and Urdu.  CA-26, CA-35, CA-91.
 *
 * - BOTH languages are fetched and joined on the item id. Painting only one
 *   left the other pane showing ANOTHER item's text beside a "Merk som
 *   gjennomgått" button — a way to certify the wrong translation.
 * - A pane with no translation is editable and visibly empty — never
 *   prefilled with a sentence that could be approved as if it were one. A
 *   language that has left the queue says "Allerede gjennomgått" — and a language whose queue could not be FETCHED
 *   says that, read-only, with «Prøv igjen» (QA C, 2026-10-16: an English failure was shown as "already reviewed").
 * - Only a pane somebody dealt with is written: approving "nothing" is not a
 *   review. Unchanged text is `approve`; changed text is `edit`.
 */
import React, { useEffect, useState } from 'react';
import { call, errText, post } from '../lib/api';
import { useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { Body as CardBody, Btn, Card, LoadError, Loading, Msg, Tag, type TagTone } from '../components/ui';

const TSTATUS: Record<string, [TagTone, string]> = { machine: ['gold', 'Maskin'], reviewed: ['green', 'Gjennomgått'], missing: ['grey', 'Ikke startet'], human: ['green', 'Oversatt'] };
const TYPE_NO: Record<string, string> = { event: 'Arrangement', notice: 'Varsel', janazah: 'Janazah', banner: 'Skjerm', notice_janazah: 'Janazah',
  campaign: 'Innsamling', khutbah: 'Khutbah' };
// What answered (2026-10-29): the mosque's own memory and the glossary are checked before any machine is asked.
const SOURCE_NO: Record<string, string> = { memory: 'moskeens oversettelsesminne', glossary: 'ordlisten', google: 'Google Translate', deepl: 'DeepL',
  human: 'en person', 'dev-marker': 'testmarkør' };
const FIELD_NO: Record<string, string> = { title: 'tittel', body: 'tekst', location: 'sted', prayer_when: 'tidspunkt', prayer_place: 'sted for bønnen',
  burial_place: 'gravsted', note: 'notat', reason: 'årsak', text: 'tekst', topic: 'tema',
  // A banner's layers arrive as `layer:<id>` — the queue printed the code (found building this view).
  'layer:headline': 'overskrift', 'layer:detail': 'tekst', 'layer:eyebrow': 'merkelapp', 'layer:when': 'når', 'layer:where': 'hvor' };

const fieldNo = (f: string) => FIELD_NO[f] || f.replace(/^layer:/, '').replace(/_/g, ' ');

type Row = { id: string; entityType: string; field: string; master: string; translated: string | null; status: string; priority: number; provider?: string | null };
type Lang = 'en' | 'ur';

export function Translations() {
  const [state, setState] = useState<{ queue: Row[]; en: Map<string, Row>; ur: Map<string, Row>; failed: Partial<Record<Lang, string>> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState<Record<Lang, string>>({ en: '', ur: '' });
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      // Urdu failing is the view failing (LoadError); English failing is said in its own pane, never read as "done".
      const failed: Partial<Record<Lang, string>> = {};
      const [ur, en] = await Promise.all([call('/translations?lang=ur'),
        call('/translations?lang=en').catch((e: Error) => { failed.en = e.message; return { queue: [] }; })]);
      const urBy = new Map<string, Row>((ur.queue || []).map((r: Row) => [r.id, r]));
      const enBy = new Map<string, Row>((en.queue || []).map((r: Row) => [r.id, r]));
      // One list of ITEMS: an item leaves the queue only when neither language needs a person.
      const ids = [...new Set([...urBy.keys(), ...enBy.keys()])];
      const queue = ids.map((id) => (urBy.get(id) || enBy.get(id)) as Row)
        .sort((a, b) => b.priority - a.priority || String(a.id).localeCompare(String(b.id)));
      setState({ queue, en: enBy, ur: urBy, failed }); setError(null);
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); }, []);

  const top = state && state.queue.length ? state.queue[Math.min(idx, state.queue.length - 1)] : null;
  const rowFor = (lang: Lang) => (top && state ? state[lang].get(top.id) || null : null);
  const enRow = rowFor('en'), urRow = rowFor('ur');
  const topId = top ? top.id : null;
  // Every pane is rewritten when the item changes — a pane left alone keeps another item's text.
  useEffect(() => { setText({ en: (enRow && enRow.translated) || '', ur: (urRow && urRow.translated) || '' }); }, [topId, state]); // eslint-disable-line react-hooks/exhaustive-deps
  const edited = Boolean(top && (text.en !== ((enRow && enRow.translated) || '') || text.ur !== ((urRow && urRow.translated) || '')));
  useDirty('translation', edited);

  if (error) return <LoadError error={error} retry={load} />;
  if (!state) return <Loading />;

  const tag = (r: Row | null | undefined) => { const [tone, label] = TSTATUS[r ? r.status : 'reviewed'] || ['grey', r ? r.status : '']; return <Tag tone={tone}>{label}</Tag>; };

  async function approve() {
    if (!top) return;
    const writes = (['en', 'ur'] as Lang[]).map((lang) => {
      const row = rowFor(lang), t = text[lang].trim(), orig = (row && row.translated) || '';
      if (!row || !t) return null;
      return { lang, action: t === orig ? 'approve' : 'edit', text: t };
    }).filter((x): x is { lang: Lang; action: string; text: string } => Boolean(x));
    if (!writes.length) return say('Skriv eller godkjenn minst én oversettelse først.', 'err');
    setBusy(true);
    const results = [];
    for (const w of writes) results.push(await post('/translations/' + encodeURIComponent(top.id), w.action === 'approve' ? { lang: w.lang, action: 'approve' } : w));
    setBusy(false);
    const bad = results.find((r) => !r.ok);
    say(bad ? errText(bad.body) : `Lagret som gjennomgått (${writes.map((w) => w.lang.toUpperCase()).join(' og ')}). Appen viser den nå.`, bad ? 'err' : 'ok');
    if (!bad) await load();
  }

  const pane = (lang: Lang, row: Row | null, label: string) => state.failed[lang] ? (
    <div>
      <h5 id={'tr-' + lang}>{label[0].toUpperCase() + label.slice(1)} — kunne ikke hentes</h5>
      <p className="msg err" role="alert">Kunne ikke hente {label}: {state.failed[lang]}</p>
      <Btn ghost onClick={() => { void load(); }}>Prøv igjen</Btn>
    </div>
  ) : (
    <div>
      <h5 id={'tr-' + lang}>{label[0].toUpperCase() + label.slice(1)} — {!row ? 'gjennomgått' : row.status === 'machine' ? 'maskin' : row.status === 'missing' ? 'mangler' : row.status}</h5>
      {row && row.translated && row.provider ? <span className="field note">Kilde: {SOURCE_NO[row.provider] || row.provider}</span> : null}
      <textarea aria-labelledby={'tr-' + lang} className={lang === 'ur' ? 'ur' : ''} dir={lang === 'ur' ? 'rtl' : 'ltr'} lang={lang}
                readOnly={!row} value={text[lang]}
                placeholder={!row ? 'Allerede gjennomgått.' : row.translated ? '' : `Ingen ${label} ennå — skriv den her.`}
                onChange={(e) => setText({ ...text, [lang]: e.target.value })} />
    </div>
  );

  return (
    <>
      <Card title={'Til gjennomgang · ' + state.queue.length} hint="Norsk er original. Engelsk og urdu er maskinoversatt og merkes som gjennomgått først når et menneske har lest dem. Til det skjer, viser appen dem med fallback urdu → engelsk → norsk.">
        <div className="trio">
          <div><h5>Norsk — original{top ? ` · ${TYPE_NO[top.entityType] || top.entityType}, ${fieldNo(top.field)}` : ''}</h5>
            <p lang="no">{top ? top.master : 'Ingenting å se gjennom.'}</p></div>
          {pane('en', enRow, 'engelsk oversettelse')}
          {pane('ur', urRow, 'urdu-oversettelse')}
        </div>
        <CardBody style={{ borderTop: '1px solid var(--hairline)' }}>
          <div className="row">
            <Btn disabled={!top || busy} onClick={approve}>Merk som gjennomgått</Btn>
            <Btn ghost disabled={!top || state.queue.length < 2} onClick={() => { setIdx((idx + 1) % state.queue.length); say(''); }}>Hopp over</Btn>
          </div>
          <Msg text={msg.text} tone={msg.tone} id="tr-msg" />
          <span className="field note" style={{ marginTop: 10, display: 'block' }}><b>Avklart 9. september:</b> alle med tilgang på administrasjonssiden kan gjennomgå urdu. Gjennomgang er ikke en sperre før publisering — innhold går ut på norsk med en gang, maskinoversettelsen merkes som maskin, og den rettes etterpå. <b>Unntaket er janazah:</b> maskinoversettelsen av en janazah vises ikke til noen før et menneske har godkjent den — til da ser alle den norske teksten.</span>
          <span className="field note" style={{ marginTop: 6, display: 'block' }}><b>Oversettelsesminne:</b> det du godkjenner eller retter her, huskes for denne moskeen og brukes neste gang samme tekst skrives — før ordlisten og før maskinoversettelsen spørres. Ingenting deles med andre moskeer.</span>
        </CardBody>
      </Card>
      <Card title="Kø">
        <table className="t">
          <thead><tr><th scope="col">Innhold</th><th scope="col">Type</th><th scope="col">Engelsk</th><th scope="col">Urdu</th></tr></thead>
          <tbody>{state.queue.length ? state.queue.map((r, i) => (
            <tr key={r.id} className={top && r.id === top.id ? 'sel' : undefined} aria-current={top && r.id === top.id ? 'true' : undefined}>
              <td><button type="button" className="rowlink" onClick={() => { setIdx(i); say(''); }}>{r.master}</button></td>
              <td>{TYPE_NO[r.entityType] || r.entityType} · {fieldNo(r.field)}</td>
              <td>{tag(state.en.get(r.id))}</td><td>{tag(state.ur.get(r.id))}</td>
            </tr>)) : <tr><td colSpan={4}>Ingenting å se gjennom. Takk!</td></tr>}</tbody>
        </table>
      </Card>
    </>
  );
}
